
/* eslint-disable @typescript-eslint/camelcase */
import { Options, Vue } from "vue-class-component";
import { firstCapitalize, formatCompactNumber as fmtCompact, formatByCountry, parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import SummaryCourierPickup from "./summary-courier-pickup.vue";
import { PickupDashboardController } from "@/app/ui/controllers/PickupDashboardController";
import { CourierSummaryRequest } from "@/data/payload/api/PickupDashboardRequests";
import HeaderFilterBar from "../../components/header-filter-bar/index.vue";
import { Range } from "@/app/ui/components/date-range-picker/type";
import formatDateNumber from "@/app/infrastructures/misc/common-library/FormatDateNumber";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { debounce } from "lodash";
import { ExportApiRequest } from "@/data/payload/api/PickupDashboardApiRequest";
import { ReportController } from "@/app/ui/controllers/ReportController";
import { DownloadReportRequest } from "@/domain/entities/Kulioner";
import DownloadWithDateRange from "@/app/ui/views/pickup-dashboard/monitoring/components/download-with-date-range/index.vue";
import { OptionsClass } from "@/domain/entities/MainApp";
import formatPhoneNumber from "@/app/infrastructures/misc/common-library/v2/FormatPhoneNumber";

@Options({
  components: { SummaryCourierPickup, HeaderFilterBar, DownloadWithDateRange }
})
class CourierDetailView extends Vue {
  formatPhoneNumber = formatPhoneNumber;

  dateRange = new Range();
  search = "";
  shipmentType: any[] = [];
  statusSummary = "BKD,CRRCNC";
  get tabs() {
    return [
      {
        name: "crrotw",
        title: `${this.$t(
          "pickupDashboard.belumDiPickUp"
        )} (${this.formatCompactNumber(this.crrotw.seller || 0)})`
      },
      {
        name: "crrpic",
        title: `${this.$t(
          "pickupDashboard.belumDisetor"
        )} (${this.formatCompactNumber(this.crrpic.shipment || 0)})`
      },
      {
        name: "crrdon",
        title: `${this.$t(
          "pickupDashboard.belumBkd"
        )} (${this.formatCompactNumber(this.crrdon.shipment || 0)})`
      },
      {
        name: "bkd",
        title: `${this.$t(
          "pickupDashboard.sudahBkd"
        )} (${this.formatCompactNumber(this.bkd.shipment || 0)})`
      },
      {
        name: "crrcnc",
        title: `${this.$t(
          "pickupDashboard.riwayatGagal"
        )} (${this.formatCompactNumber(this.crrcnc.seller || 0)})`
      }
    ];
  }

  get shipmentTypes() {
    return [
      new OptionsClass({
        name: "AGGREGATOR",
        value: "AGGREGATOR"
      }),
      new OptionsClass({
        name: "MARKETPLACE",
        value: "MARKETPLACE"
      }),
      new OptionsClass({
        name: "CUSTOMER APP",
        value: "CUSTOMER_APP"
      }),
      new OptionsClass({
        name: "CORPORATE",
        value: "CORPORATE"
      })
    ];
  }

  tabKey = 0;

  get isLoadingSummary() {
    return PickupDashboardController.isLoadingCourierSummary;
  }

  get crrotw() {
    return {
      seller: PickupDashboardController.crrotw.seller || 0,
      shipment: PickupDashboardController.crrotw.shipment || 0,
      quantity: PickupDashboardController.crrotw.quantity || 0
    } as any;
  }

  get crrpic() {
    return {
      shipment: PickupDashboardController.crrpic.shipment || 0,
      quantity: PickupDashboardController.crrpic.quantity || 0
    } as any;
  }

  get crrdon() {
    return {
      shipment: PickupDashboardController.crrdon.shipment || 0,
      quantity: PickupDashboardController.crrdon.quantity || 0
    } as any;
  }

  get bkd() {
    return {
      seller: PickupDashboardController.bkd.seller || 0,
      shipment: PickupDashboardController.bkd.shipment || 0,
      quantity: PickupDashboardController.bkd.quantity || 0
    } as any;
  }

  get crrcnc() {
    return {
      seller: PickupDashboardController.crrcnc.seller || 0,
      shipment: PickupDashboardController.crrcnc.shipment || 0,
      quantity: PickupDashboardController.crrcnc.quantity || 0
    } as any;
  }

  get id(): string {
    return String(this.$route.params.id || "");
  }

  get date(): string {
    return String(this.$route.query.date || "");
  }

  get courierName(): string {
    return String(this.$route.query.courier_name || "");
  }

  get courierPhone(): string {
    return String(this.$route.query.courier_phone || "");
  }

  get courierDisplayInfo() {
    const displayName = this.courierName
      ? this.courierName
          .split(" ")
          .map((w) => firstCapitalize(w))
          .join(" ")
      : "";
    const parts = [
      displayName,
      this.id,
      this.formatPhoneNumber(this.courierPhone)
    ].filter((v) => !!v);
    return parts.join(" • ");
  }

  mounted() {
    this.redirectBaseToDefaultTab();
    const startQ = String(this.$route.query.start_date || "");
    const endQ = String(this.$route.query.end_date || "");
    const shipmentTypeQ = String(this.$route.query.shipment_type || "");

    if (startQ && endQ) {
      try {
        const s = new Date(startQ);
        const e = new Date(endQ);
        this.dateRange = new Range({ start: s, end: e });
      } catch (_) {
        // ignore parse error
      }
    }

    // Parse shipment type from query parameters
    if (shipmentTypeQ) {
      const shipmentTypeValues = shipmentTypeQ.split(",");
      this.shipmentType = shipmentTypeValues.map((value) => ({
        name: this.shipmentTypes.find((s) => s.value === value)?.name || value,
        value: value
      }));
    }

    const startStr = startQ ? startQ : "";
    const endStr = endQ ? endQ : "";
    const q = this.search || "";
    const st = this.processShipmentType(this.shipmentType);
    PickupDashboardController.setFilter({
      startDate: startStr,
      endDate: endStr,
      q,
      shipmentType: st,
      courierId: Number(this.id)
    });
    this.fetchSummary();
    this.fetchBkdCrrcncSummary();

    this.$watch(
      () => this.dateRange,
      () => {
        this.applyFilters();
      },
      { deep: true }
    );
    this.$watch(
      () => this.shipmentType,
      () => {
        this.applyFilters();
      },
      { deep: true }
    );
  }

  redirectBaseToDefaultTab() {
    const basePath = `/pickup-dashboard/monitoring/courier/${this.id}`;
    if (this.$route.path === basePath) {
      this.$router.replace({
        path: `${basePath}/crrotw`,
        query: this.currentFilterQuery
      });
      return;
    }
  }

  isTabActive(name: string) {
    return this.$route.path.endsWith(`/${name}`);
  }

  changeTabs(name: string) {
    this.tabKey++;
    this.$router.push({
      path: `/pickup-dashboard/monitoring/courier/${this.id}/${name}`,
      query: this.currentFilterQuery
    });
  }

  goBack() {
    const path = "/pickup-dashboard/monitoring/courier";
    this.$router.push(path);
  }

  onDownloadPickUp() {
    // method download
  }

  async fetchSummary() {
    const cf = PickupDashboardController.currentFilter;
    const start = cf.startDate;
    const end = cf.endDate;
    const params = new CourierSummaryRequest({
      startDate: typeof start === "string" ? start : formatDateNumber(start),
      endDate: typeof end === "string" ? end : formatDateNumber(end),
      courierId: Number(cf.courierId || this.id),
      status: "CRROTW,CRRPIC,CRRDON",
      q: String(cf.q || ""),
      shipmentType: String(cf.shipmentType || "")
    });
    await PickupDashboardController.getCourierSummary(params);
  }

  async fetchBkdCrrcncSummary() {
    const cf = PickupDashboardController.currentFilter;
    const params = new CourierSummaryRequest({
      startDate: formatDateNumber(new Date()),
      endDate: formatDateNumber(new Date()),
      courierId: Number(cf.courierId || this.id),
      status: this.statusSummary,
      q: String(cf.q || ""),
      shipmentType: String(cf.shipmentType || "")
    });
    await PickupDashboardController.getCourierSummary(params);
  }

  formatCompactNumber(val: number) {
    return fmtCompact(val);
  }

  processShipmentType(shipmentType: any): string {
    if (!shipmentType) return "";
    if (Array.isArray(shipmentType)) {
      return shipmentType.map((s: any) => s?.value || s).join(",");
    }
    if (typeof shipmentType === "string") {
      return shipmentType;
    }
    return "";
  }

  getTabCount(name: string) {
    if (name === "crrotw") return formatByCountry(this.crrotw.seller || 0, { type: "quantity" });
    if (name === "crrpic") return formatByCountry(this.crrpic.shipment || 0, { type: "quantity" });
    if (name === "crrdon") return formatByCountry(this.crrdon.shipment || 0, { type: "quantity" });
    if (name === "bkd") return formatByCountry(this.bkd.shipment || 0, { type: "quantity" });
    if (name === "crrcnc") return formatByCountry(this.crrcnc.seller || 0, { type: "quantity" });
    return 0;
  }

  get currentFilterQuery() {
    const start = (this.dateRange as any)?.start;
    const end = (this.dateRange as any)?.end;
    const startStr = start
      ? typeof start === "string"
        ? start
        : formatDateNumber(start)
      : "";
    const endStr = end
      ? typeof end === "string"
        ? end
        : formatDateNumber(end)
      : "";
    const q = this.search || "";
    const st = this.processShipmentType(this.shipmentType);
    const base: Record<string, string> = {
      courier_name: this.courierName,
      courier_phone: this.courierPhone,
      start_date: startStr,
      end_date: endStr
    };
    if (q) base.q = q;
    if (st) base.shipment_type = st;
    return base;
  }

  applyFilters() {
    const start = (this.dateRange as any)?.start;
    const end = (this.dateRange as any)?.end;
    const startStr = start
      ? typeof start === "string"
        ? start
        : formatDateNumber(start)
      : "";
    const endStr = end
      ? typeof end === "string"
        ? end
        : formatDateNumber(end)
      : "";
    const q = this.search || "";
    const st = this.processShipmentType(this.shipmentType);
    PickupDashboardController.setFilter({
      startDate: startStr,
      endDate: endStr,
      q,
      shipmentType: st,
      courierId: Number(this.id)
    });
    const newQuery = this.currentFilterQuery;
    this.$router.replace({
      path: this.$route.path,
      query: newQuery
    });
    // Removed tabKey++ to prevent unnecessary re-renders
    if (this.isTabActive("bkd")) {
      this.statusSummary = "BKD";
      this.fetchBkdCrrcncSummary();
    } else if (this.isTabActive("crrcnc")) {
      this.statusSummary = "CRRCNC";
      this.fetchBkdCrrcncSummary();
    } else {
      this.fetchSummary();
    }
  }

  onFilterSearch() {
    this.applyFilters();
  }

  onDownload = debounce(async (date: DownloadReportRequest) => {
    const cf = PickupDashboardController.currentFilter;
    try {
      MainAppController.showLoading();

      await PickupDashboardController.export(
        new ExportApiRequest({
          ...date,
          q: cf.q,
          shipmentType: this.processShipmentType(cf.shipmentType),
          courierId: Number(cf.courierId || this.id)
        })
      );

      await ReportController.onGetListDownloadReport();
      ReportController.setOpenDownloadRequest(true);
    } catch (err) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(err, "Gagal Download Data !", this.onDownload)
      );
    } finally {
      MainAppController.closeLoading();
    }
  }, 400);
}
export default CourierDetailView;
